import { supabase } from "@/lib/supabase-client";
import { TimecardType, TIME_MODIFIERS, TimeModifierCode } from "@/types/db/time_cards";
import { FileMetadata } from "@/types/files";
import { FromDB } from "@/types";

import { resolveEffectivePayrollRate, calculateAmountsFromSegments } from "@/lib/domain/timekeeping";

// Re-export the timekeeping domain helpers (public API preserved).
export {
  getModifierMultiplier,
  extractBillableSegments,
  extractPayableSegments,
  getSegmentDuration,
} from "@/lib/domain/timekeeping";
export type { TimeSegment } from "@/lib/domain/timekeeping";

export const timecardService = {
  async getTimecards(): Promise<FromDB<TimecardType>[]> {
    const startTotal = performance.now();

    const startTimeCards = performance.now();
    const { data: rawTimeCards, error: timeCardsError } = await supabase
      .from("time_cards")
      .select(
        `id,
        status,
        shift_id,
        client_id,
        project_id,
        time,
        meta_data,
        user: users(id, first_name, last_name, preferred_name),
        shift_user: shift_users(
          status,
          shift_position: shift_positions(
            id,
            position_id,
            shift: shifts(
              id,
              name,
              location_id,
              meta_data,
              project: projects(
                id,
                name,
                status,
                location_id,
                meta_data,
                client: clients(id, name)
              )
            )
          )
        )`,
      )
      .limit(9000);
    const endTimeCards = performance.now();

    if (timeCardsError) {
      console.error("TimeCards fetch error:", timeCardsError);
      throw timeCardsError;
    }

    // Transform to match expected structure with redundant keys
    const timeCards = rawTimeCards?.map((tc: any) => {
      const shiftPosition = tc.shift_user?.shift_position;
      const shift = shiftPosition?.shift;
      const project = shift?.project;
      const client = project?.client;
      const metaData = tc.meta_data || {};

      return {
        id: tc.id,
        // Project fields first for default column ordering
        project: project ? { name: project.name, id: project.id } : null,
        project_status: project?.status || null,
        project_billing_status: (project?.meta_data as any)?.billing_status || null,
        status: tc.status,
        // billing: tc.billing,
        shift_id: tc.shift_id,
        client_id: tc.client_id,
        project_id: tc.project_id,
        time: tc.time,
        meta_data: metaData, // Preserve meta_data for rate overrides
        notes: metaData.note || null,
        // modifiers: tc.modifiers,
        user: tc.user
          ? {
              name: (tc.user.preferred_name || tc.user.first_name) + " " + tc.user.last_name,
              id: tc.user.id,
              first_name: tc.user.first_name,
              last_name: tc.user.last_name,
            }
          : null,
        shift_user_status: tc.shift_user?.status || null,
        // Snapshots from meta_data (captured at timecard creation)
        position_snapshot: metaData.position_snapshot || null,
        user_pay_snapshot: metaData.user_pay_snapshot || null,
        // Preserve original nested structure for compatibility
        client: client ? { name: client.name, id: client.id } : null,
        shift: shift ? { name: shift.name, id: shift.id } : null,
        position_id: { shift_positions: { position_id: shiftPosition?.position_id } },
      };
    });

    // Extract unique position_ids from the nested structure
    const positionIds = new Set<string>();
    timeCards?.forEach((tc: any) => {
      const positionId = tc.position_id?.shift_positions?.position_id;
      if (positionId) {
        positionIds.add(positionId);
      }
    });

    // Query lists.positions for position names
    const startPositions = performance.now();
    let positionsMap = new Map<string, any>();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("*")
        .in("id", Array.from(positionIds));

      if (positions) {
        positions.forEach((p) => {
          positionsMap.set(p.id, p);
        });
      }
    }
    const endPositions = performance.now();

    // Collect location_ids and market_ids from shifts and projects for market inheritance
    // Must use rawTimeCards here since the transform step loses nested shift/project data
    const locationIds = new Set<string>();
    const additionalMarketIds = new Set<string>();
    rawTimeCards?.forEach((tc: any) => {
      const shiftPosition = tc.shift_user?.shift_position;
      const shift = shiftPosition?.shift;
      const project = shift?.project;

      // Collect location_ids
      if (shift?.location_id) locationIds.add(shift.location_id);
      if (project?.location_id) locationIds.add(project.location_id);

      // Collect additional market_ids from meta_data.markets
      const shiftMarkets = (shift?.meta_data as any)?.markets || [];
      const projectMarkets = (project?.meta_data as any)?.markets || [];
      shiftMarkets.forEach((id: string) => additionalMarketIds.add(id));
      projectMarkets.forEach((id: string) => additionalMarketIds.add(id));
    });

    // Fetch location->market_id mappings
    const startMarkets = performance.now();
    let locationMarketMap = new Map<string, string>(); // location_id -> market_id
    if (locationIds.size > 0) {
      const { data: locations } = await supabase
        .schema("lists")
        .from("locations")
        .select("id, market_id")
        .in("id", Array.from(locationIds));

      locations?.forEach((loc) => {
        if (loc.market_id) {
          locationMarketMap.set(loc.id, loc.market_id);
          additionalMarketIds.add(loc.market_id); // Also fetch market details
        }
      });
    }

    // Fetch all market details
    let marketsMap = new Map<string, { id: string; name: string; fips_code?: string }>();
    if (additionalMarketIds.size > 0) {
      const { data: markets } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name, fips_code")
        .in("id", Array.from(additionalMarketIds));

      markets?.forEach((m) => {
        marketsMap.set(m.id, m);
      });
    }
    const endMarkets = performance.now();

    // Create a map from timecard_id to shift/project data for market computation
    const timecardShiftProjectMap = new Map<
      string,
      { shiftLocationId?: string; projectLocationId?: string; shiftMarkets: string[]; projectMarkets: string[] }
    >();
    rawTimeCards?.forEach((tc: any) => {
      const shiftPosition = tc.shift_user?.shift_position;
      const shift = shiftPosition?.shift;
      const project = shift?.project;
      timecardShiftProjectMap.set(tc.id, {
        shiftLocationId: shift?.location_id,
        projectLocationId: project?.location_id,
        shiftMarkets: (shift?.meta_data as any)?.markets || [],
        projectMarkets: (project?.meta_data as any)?.markets || [],
      });
    });

    // Fetch all billing_items and payroll_items to check for linked timecards
    const startBillingItems = performance.now();
    const [{ data: billingItems }, { data: payrollItems }] = await Promise.all([
      supabase.from("billing_items").select("id, meta_data"),
      supabase.from("payroll_items").select("id, meta_data"),
    ]);
    const endBillingItems = performance.now();

    // Create a map of timecard_id -> billing_items
    const billingItemsMap = new Map<string, any[]>();
    billingItems?.forEach((billingItem) => {
      const linkedTimeCards = billingItem.meta_data?.links?.time_cards;
      if (Array.isArray(linkedTimeCards)) {
        linkedTimeCards.forEach((timeCardId: string) => {
          if (!billingItemsMap.has(timeCardId)) {
            billingItemsMap.set(timeCardId, []);
          }
          billingItemsMap.get(timeCardId)?.push(billingItem);
        });
      }
    });

    // Create a map of timecard_id -> payroll_items
    const payrollItemsMap = new Map<string, any[]>();
    payrollItems?.forEach((payrollItem) => {
      const linkedTimeCards = payrollItem.meta_data?.links?.time_cards;
      if (Array.isArray(linkedTimeCards)) {
        linkedTimeCards.forEach((timeCardId: string) => {
          if (!payrollItemsMap.has(timeCardId)) {
            payrollItemsMap.set(timeCardId, []);
          }
          payrollItemsMap.get(timeCardId)?.push(payrollItem);
        });
      }
    });

    // Merge position data, billing_items, and payroll_items back into timeCards
    const startEnrich = performance.now();
    const enrichedTimeCards = timeCards?.map((tc: any) => {
      // Use position_snapshot from meta_data first, fall back to lookup for older records
      const positionId = tc.position_id?.shift_positions?.position_id;
      const positionFromLookup = positionId ? positionsMap.get(positionId) : null;
      const position = tc.position_snapshot || positionFromLookup;
      const linkedBillingItems = billingItemsMap.get(tc.id) || [];
      const linkedPayrollItems = payrollItemsMap.get(tc.id) || [];

      // Position's billing rate (from snapshot)
      const billingRateSnapshot =
        tc.position_snapshot?.meta_data?.billing_rate ||
        tc.position_snapshot?.billing_rate ||
        position?.meta_data?.billing_rate ||
        null;

      // Position's payroll rate formula (from snapshot)
      const payrollRateSnapshot =
        tc.position_snapshot?.meta_data?.payroll_rate ||
        tc.position_snapshot?.payroll_rate ||
        position?.meta_data?.payroll_rate ||
        null;

      // User's pay rate from snapshot (number = base rate, object = per-position override)
      const rawUserPayRate = tc.user_pay_snapshot?.meta_data?.rate;
      const userPayRate = typeof rawUserPayRate === "number" ? rawUserPayRate : rawUserPayRate?.amount || null;

      // Resolve effective payroll rate from position formula + user pay
      const effectivePayrollRate = resolveEffectivePayrollRate(payrollRateSnapshot, tc.user_pay_snapshot);

      // Check for flat rate overrides in meta_data
      const billingRateOverride = tc.meta_data?.billing_rate_override || null;
      const payrollRateOverride = tc.meta_data?.payroll_rate_override || null;

      // Current rates: override if set, otherwise snapshot
      const currentBillingRate = billingRateOverride || billingRateSnapshot;
      const currentPayrollRate = payrollRateOverride || payrollRateSnapshot;

      // Rates object for display in RateEditor
      const rates = {
        billing_rate: currentBillingRate,
        payroll_rate: currentPayrollRate,
        billing_rate_snapshot: billingRateSnapshot,
        payroll_rate_snapshot: payrollRateSnapshot,
        user_pay_rate: userPayRate,
      };

      // Compute billing_amount and payroll_amount
      let billingAmount: number | null = null;
      let payrollAmount: number | null = null;

      // Flat override = direct amount, no calculation
      if (billingRateOverride?.rate === "flat") {
        billingAmount = billingRateOverride.amount;
      }
      if (payrollRateOverride?.rate === "flat") {
        payrollAmount = payrollRateOverride.amount;
      }

      // Calculate non-flat amounts from segments
      if (billingAmount === null || payrollAmount === null) {
        const calculated = calculateAmountsFromSegments(
          tc.time,
          billingAmount === null ? billingRateSnapshot : null,
          payrollAmount === null ? effectivePayrollRate : null,
        );
        if (billingAmount === null) billingAmount = calculated.billingAmount;
        if (payrollAmount === null) payrollAmount = calculated.payrollAmount;
      }

      // Compute inherited markets from shift->project chain (as string names for table display)
      const shiftProjectData = timecardShiftProjectMap.get(tc.id);
      const inheritedMarketNames: string[] = [];
      const seenMarketIds = new Set<string>();

      if (shiftProjectData) {
        // Location market (shift's location takes precedence over project's)
        const locationId = shiftProjectData.shiftLocationId || shiftProjectData.projectLocationId;
        if (locationId) {
          const marketId = locationMarketMap.get(locationId);
          if (marketId && !seenMarketIds.has(marketId)) {
            const market = marketsMap.get(marketId);
            if (market) {
              inheritedMarketNames.push(market.name);
              seenMarketIds.add(marketId);
            }
          }
        }

        // Shift's additional markets
        shiftProjectData.shiftMarkets.forEach((marketId) => {
          if (!seenMarketIds.has(marketId)) {
            const market = marketsMap.get(marketId);
            if (market) {
              inheritedMarketNames.push(market.name);
              seenMarketIds.add(marketId);
            }
          }
        });

        // Project's additional markets
        shiftProjectData.projectMarkets.forEach((marketId) => {
          if (!seenMarketIds.has(marketId)) {
            const market = marketsMap.get(marketId);
            if (market) {
              inheritedMarketNames.push(market.name);
              seenMarketIds.add(marketId);
            }
          }
        });
      }

      return {
        ...tc,
        // Derived time fields from time JSONB
        start_time: tc.time?.start_time || null,
        end_time: tc.time?.end_time || null,
        position: position?.name || null,
        position_data: position || null, // Full position object for copy functions
        // Combined rates object for RateEditor (before computed amounts)
        rates,
        billing_amount: billingAmount,
        payroll_amount: payrollAmount,
        billing_items: linkedBillingItems,
        payroll_items: linkedPayrollItems,
        // Inherited markets from shift->project chain (as names for table display)
        markets: inheritedMarketNames,
      };
    });
    const endEnrich = performance.now();

    const endTotal = performance.now();

    console.log(`[getTimecards] Timing breakdown:
  - time_cards query: ${(endTimeCards - startTimeCards).toFixed(2)}ms (${rawTimeCards?.length ?? 0} records)
  - positions query: ${(endPositions - startPositions).toFixed(2)}ms (${positionIds.size} positions)
  - markets query: ${(endMarkets - startMarkets).toFixed(2)}ms (${locationIds.size} locations, ${additionalMarketIds.size} markets)
  - billing_items query: ${(endBillingItems - startBillingItems).toFixed(2)}ms (${billingItems?.length ?? 0} items)
  - enrichment: ${(endEnrich - startEnrich).toFixed(2)}ms
  - TOTAL: ${(endTotal - startTotal).toFixed(2)}ms`);

    return enrichedTimeCards;
  },

  async getTimecardsRPC(includeArchived = false): Promise<FromDB<TimecardType>[]> {
    const start = performance.now();

    const [{ data, error }, { data: billingItems }, { data: payrollItems }] = await Promise.all([
      supabase.rpc("get_timecards", { p_include_archived: includeArchived }),
      supabase.from("billing_items").select("id, meta_data"),
      supabase.from("payroll_items").select("id, meta_data"),
    ]);

    if (error) {
      console.error("getTimecardsRPC error:", error);
      throw error;
    }

    const rows = (data as any[]) || [];

    const billingItemsMap = new Map<string, any[]>();
    billingItems?.forEach((bi) => {
      const linkedTCs = bi.meta_data?.links?.time_cards;
      if (Array.isArray(linkedTCs)) {
        linkedTCs.forEach((tcId: string) => {
          if (!billingItemsMap.has(tcId)) billingItemsMap.set(tcId, []);
          billingItemsMap.get(tcId)!.push(bi);
        });
      }
    });

    const payrollItemsMap = new Map<string, any[]>();
    payrollItems?.forEach((pi) => {
      const linkedTCs = pi.meta_data?.links?.time_cards;
      if (Array.isArray(linkedTCs)) {
        linkedTCs.forEach((tcId: string) => {
          if (!payrollItemsMap.has(tcId)) payrollItemsMap.set(tcId, []);
          payrollItemsMap.get(tcId)!.push(pi);
        });
      }
    });

    // Rate enrichment stays client-side (complex override/snapshot logic)
    const enriched = rows.map((tc: any) => {
      const positionData = tc.position_data;
      const billingRateSnapshot =
        tc.position_snapshot?.meta_data?.billing_rate ||
        tc.position_snapshot?.billing_rate ||
        positionData?.meta_data?.billing_rate ||
        null;

      const payrollRateSnapshot =
        tc.position_snapshot?.meta_data?.payroll_rate ||
        tc.position_snapshot?.payroll_rate ||
        positionData?.meta_data?.payroll_rate ||
        null;

      const rawUserPayRate = tc.user_pay_snapshot?.meta_data?.rate;
      const userPayRate = typeof rawUserPayRate === "number" ? rawUserPayRate : rawUserPayRate?.amount || null;

      const effectivePayrollRate = resolveEffectivePayrollRate(payrollRateSnapshot, tc.user_pay_snapshot);

      const billingRateOverride = tc.meta_data?.billing_rate_override || null;
      const payrollRateOverride = tc.meta_data?.payroll_rate_override || null;

      const currentBillingRate = billingRateOverride || billingRateSnapshot;
      const currentPayrollRate = payrollRateOverride || payrollRateSnapshot;

      const rates = {
        billing_rate: currentBillingRate,
        payroll_rate: currentPayrollRate,
        billing_rate_snapshot: billingRateSnapshot,
        payroll_rate_snapshot: payrollRateSnapshot,
        user_pay_rate: userPayRate,
      };

      let billingAmount: number | null = null;
      let payrollAmount: number | null = null;

      if (billingRateOverride?.rate === "flat") {
        billingAmount = billingRateOverride.amount;
      }
      if (payrollRateOverride?.rate === "flat") {
        payrollAmount = payrollRateOverride.amount;
      }

      if (billingAmount === null || payrollAmount === null) {
        const calculated = calculateAmountsFromSegments(
          tc.time,
          billingAmount === null ? billingRateSnapshot : null,
          payrollAmount === null ? effectivePayrollRate : null,
        );
        if (billingAmount === null) billingAmount = calculated.billingAmount;
        if (payrollAmount === null) payrollAmount = calculated.payrollAmount;
      }

      return {
        ...tc,
        rates,
        billing_amount: billingAmount,
        payroll_amount: payrollAmount,
        billing_items: billingItemsMap.get(tc.id) || [],
        payroll_items: payrollItemsMap.get(tc.id) || [],
      };
    });

    const duration = performance.now() - start;
    console.log(`[getTimecardsRPC] ${rows.length} records in ${duration.toFixed(2)}ms`);

    return enriched;
  },

  async addTimecard(timecardData: Partial<TimecardType>): Promise<TimecardType> {
    const { data, error } = await supabase.from("time_cards").insert(timecardData).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as TimecardType;
  },

  async saveRowPatches(patches: { id: string; [key: string]: any }[]): Promise<void> {
    // Check if any patches contain nested objects (like meta_data) that need merging
    const patchesWithNestedObjects = patches.filter((patch) =>
      Object.values(patch).some(
        (v) => typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date),
      ),
    );

    // If we have nested objects, we need to fetch current values and merge
    if (patchesWithNestedObjects.length > 0) {
      const idsToFetch = patchesWithNestedObjects.map((p) => p.id);
      const { data: currentRows, error: fetchError } = await supabase
        .from("time_cards")
        .select("id, meta_data")
        .in("id", idsToFetch);

      if (fetchError) {
        throw new Error(`Failed to fetch current values: ${fetchError.message}`);
      }

      const currentDataMap = new Map(currentRows?.map((row) => [row.id, row]) || []);

      // Merge nested objects with current values
      for (const patch of patchesWithNestedObjects) {
        const currentRow = currentDataMap.get(patch.id);
        if (currentRow && patch.meta_data) {
          patch.meta_data = { ...(currentRow.meta_data || {}), ...patch.meta_data };
        }
      }
    }

    const promises = patches.map(({ id, ...fields }) => supabase.from("time_cards").update(fields).eq("id", id));

    const results = await Promise.all(promises);
    const errors = results.filter((result) => result.error);

    if (errors.length > 0) {
      console.error(
        "Timecard update errors:",
        errors.map((e) => e.error),
      );
      const firstError = errors[0]?.error;
      throw new Error(`Failed to update ${errors.length} timecard(s): ${firstError?.message || "Unknown error"}`);
    }
  },

  async deleteTimecardById(timecardId: string): Promise<boolean> {
    const { error } = await supabase.from("time_cards").delete().eq("id", timecardId);
    if (error) {
      throw new Error(error.message);
    }
    return true;
  },

  async deleteTimecardsByIds(timecardIds: string[]): Promise<void> {
    const BATCH_SIZE = 10; // Small batches to isolate timeout issues
    const DEBUG = true; // Set to false to disable diagnostics

    const totalStart = performance.now();
    if (DEBUG) {
      console.log(`[TC-DELETE] Starting delete of ${timecardIds.length} timecards`);
      console.log(`[TC-DELETE] IDs: ${timecardIds.slice(0, 5).join(", ")}${timecardIds.length > 5 ? "..." : ""}`);
    }

    // Process in batches to identify where timeout occurs
    for (let i = 0; i < timecardIds.length; i += BATCH_SIZE) {
      const batch = timecardIds.slice(i, i + BATCH_SIZE);
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      const totalBatches = Math.ceil(timecardIds.length / BATCH_SIZE);

      const batchStart = performance.now();
      if (DEBUG) {
        console.log(`[TC-DELETE] Batch ${batchNum}/${totalBatches}: Deleting ${batch.length} timecards...`);
      }

      const { error } = await supabase.from("time_cards").delete().in("id", batch);

      const batchDuration = performance.now() - batchStart;
      if (DEBUG) {
        console.log(
          `[TC-DELETE] Batch ${batchNum}/${totalBatches}: ${error ? "FAILED" : "OK"} in ${batchDuration.toFixed(0)}ms`,
        );
      }

      if (error) {
        const totalDuration = performance.now() - totalStart;
        console.error(`[TC-DELETE] FAILED at batch ${batchNum} after ${totalDuration.toFixed(0)}ms total`);
        console.error(`[TC-DELETE] Error: ${error.message}`);
        throw new Error(`Failed to delete timecards (batch ${batchNum}): ${error.message}`);
      }
    }

    const totalDuration = performance.now() - totalStart;
    if (DEBUG) {
      console.log(`[TC-DELETE] Complete: ${timecardIds.length} timecards deleted in ${totalDuration.toFixed(0)}ms`);
      console.log(`[TC-DELETE] Avg per timecard: ${(totalDuration / timecardIds.length).toFixed(1)}ms`);
    }
  },

  async getUnapprovedTimecardsCount(): Promise<number> {
    const { count, error } = await supabase
      .from("time_cards")
      .select("*", { count: "exact", head: true })
      .neq("status", "APPROVED");

    if (error) {
      throw new Error(`Error fetching unapproved timecards count: ${error.message}`);
    }
    return count || 0;
  },

  async getByShiftAndUser(shiftId: string, userId: string): Promise<TimecardType | null> {
    const { data, error } = await supabase
      .from("time_cards")
      .select("*")
      .eq("shift_id", shiftId)
      .eq("user_id", userId)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // No rows returned - this is expected if no timecard exists
        return null;
      }
      throw new Error(`Failed to fetch timecard: ${error.message}`);
    }

    return data as TimecardType;
  },

  async updateTimecard(timecardId: string, updates: Partial<TimecardType>): Promise<TimecardType> {
    const { data, error } = await supabase.from("time_cards").update(updates).eq("id", timecardId).select().single();

    if (error) {
      throw new Error(`Failed to update timecard: ${error.message}`);
    }

    return data as TimecardType;
  },

  async getTimecardById(id: string): Promise<any | null> {
    // 1. Fetch timecard with same query pattern as getTimecards()
    const { data: tc, error } = await supabase
      .from("time_cards")
      .select(
        `id,
        status,
        billing,
        shift_id,
        client_id,
        project_id,
        time,
        created_at,
        updated_at,
        created_by,
        updated_by,
        notes: meta_data->>note,
        user: users(id, first_name, last_name, preferred_name),
        shift_user: shift_users(
          shift_position: shift_positions(
            id,
            position_id,
            shift: shifts(
              id,
              name,
              project: projects(
                id,
                name,
                status,
                client: clients(id, name)
              )
            )
          )
        )`,
      )
      .eq("id", id)
      .single();

    if (error || !tc) return null;

    // 2. Transform nested data (same as getTimecards)
    const shiftPosition = (tc as any).shift_user?.shift_position;
    const shift = shiftPosition?.shift;
    const project = shift?.project;
    const client = project?.client;

    // 3. Fetch position data for enrichment
    let position = null;
    let billing_rate = null;
    const positionId = shiftPosition?.position_id;
    if (positionId) {
      const { data: positionData } = await supabase
        .schema("lists")
        .from("positions")
        .select("name, billing_rate")
        .eq("id", positionId)
        .single();
      if (positionData) {
        position = positionData.name;
        billing_rate = positionData.billing_rate;
      }
    }

    // 4. Calculate hours_worked
    let hours_worked: number | null = null;
    const time = (tc as any).time;
    if (time?.start_time && time?.end_time) {
      const startTime = new Date(time.start_time);
      const endTime = new Date(time.end_time);
      let totalMs = endTime.getTime() - startTime.getTime();
      if (time.meal_start && time.meal_end) {
        const mealStart = new Date(time.meal_start);
        const mealEnd = new Date(time.meal_end);
        totalMs -= mealEnd.getTime() - mealStart.getTime();
      }
      hours_worked = totalMs / (1000 * 60 * 60);
    }

    // 5. Return enriched data matching getTimecards() shape
    return {
      id: (tc as any).id,
      status: (tc as any).status,
      billing: (tc as any).billing,
      shift_id: (tc as any).shift_id,
      client_id: (tc as any).client_id,
      project_id: (tc as any).project_id,
      time: (tc as any).time,
      notes: (tc as any).notes,
      user: (tc as any).user
        ? {
            name: ((tc as any).user.preferred_name || (tc as any).user.first_name) + " " + (tc as any).user.last_name,
            id: (tc as any).user.id,
            first_name: (tc as any).user.first_name,
            last_name: (tc as any).user.last_name,
          }
        : null,
      client: client ? { name: client.name, id: client.id } : null,
      shift: shift ? { name: shift.name, id: shift.id } : null,
      project: project ? { name: project.name, id: project.id } : null,
      // Enriched fields
      position,
      billing_rate,
      hours_worked,
    };
  },

  async getTimecardsByUserId(userId: string): Promise<TimecardType[]> {
    const { data, error } = await supabase
      .from("time_cards")
      .select(
        `id,
        status,
        billing,
        time,
        user_id,
        notes: meta_data->>note,
        shift_user: shift_users(
          shift_position: shift_positions(
            id,
            position_id,
            shift: shifts(
              id,
              name,
              start_time,
              end_time,
              project: projects(
                id,
                name,
                client: clients(id, name)
              )
            )
          )
        )`,
      )
      .eq("user_id", userId);

    if (error) {
      throw new Error(`Failed to fetch timecards for user ${userId}: ${error.message}`);
    }

    // Extract unique position_ids for lookup
    const positionIds = new Set<string>();
    data?.forEach((tc: any) => {
      const positionId = tc.shift_user?.shift_position?.position_id;
      if (positionId) positionIds.add(positionId);
    });

    // Fetch position names from lists.positions
    let positionsMap = new Map<string, any>();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .in("id", Array.from(positionIds));
      positions?.forEach((p) => positionsMap.set(p.id, p));
    }

    // Transform to clean flat structure
    const transformed = data?.map((tc: any) => {
      const shiftPosition = tc.shift_user?.shift_position;
      const shift = shiftPosition?.shift;
      const project = shift?.project;
      const client = project?.client;
      const position = shiftPosition?.position_id ? positionsMap.get(shiftPosition.position_id) : null;

      // Calculate hours_worked
      let hours_worked: number | null = null;
      if (tc.time?.start_time && tc.time?.end_time) {
        const start = new Date(tc.time.start_time);
        const end = new Date(tc.time.end_time);
        let totalMs = end.getTime() - start.getTime();
        if (tc.time.meal_start && tc.time.meal_end) {
          totalMs -= new Date(tc.time.meal_end).getTime() - new Date(tc.time.meal_start).getTime();
        }
        hours_worked = totalMs / (1000 * 60 * 60);
      }

      return {
        id: tc.id,
        status: tc.status,
        billing: tc.billing,
        time: tc.time,
        user_id: tc.user_id,
        notes: tc.notes,
        shift: shift
          ? { id: shift.id, name: shift.name, start_time: shift.start_time, end_time: shift.end_time }
          : null,
        project: project ? { id: project.id, name: project.name } : null,
        client: client ? { id: client.id, name: client.name } : null,
        position: position ? { id: position.id, name: position.name } : null,
        hours_worked,
      };
    });

    return (transformed || []) as unknown as TimecardType[];
  },

  async getTimecardsByProjectId(projectId: string): Promise<TimecardType[]> {
    const { data, error } = await supabase
      .from("time_cards")
      .select(
        `id,
        status,
        billing,
        time,
        user_id,
        project_id,
        notes: meta_data->>note,
        user: users(id, first_name, last_name, preferred_name),
        shift_user: shift_users(
          shift_position: shift_positions(
            id,
            position_id,
            shift: shifts(
              id,
              name,
              start_time,
              end_time
            )
          )
        )`,
      )
      .eq("project_id", projectId);

    if (error) {
      throw new Error(`Failed to fetch timecards for project ${projectId}: ${error.message}`);
    }

    // Extract unique position_ids for lookup
    const positionIds = new Set<string>();
    data?.forEach((tc: any) => {
      const positionId = tc.shift_user?.shift_position?.position_id;
      if (positionId) positionIds.add(positionId);
    });

    // Fetch position names from lists.positions
    let positionsMap = new Map<string, any>();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .in("id", Array.from(positionIds));
      positions?.forEach((p) => positionsMap.set(p.id, p));
    }

    // Transform to clean flat structure
    const transformed = data?.map((tc: any) => {
      const shiftPosition = tc.shift_user?.shift_position;
      const shift = shiftPosition?.shift;
      const position = shiftPosition?.position_id ? positionsMap.get(shiftPosition.position_id) : null;

      // Calculate hours_worked
      let hours_worked: number | null = null;
      if (tc.time?.start_time && tc.time?.end_time) {
        const start = new Date(tc.time.start_time);
        const end = new Date(tc.time.end_time);
        let totalMs = end.getTime() - start.getTime();
        if (tc.time.meal_start && tc.time.meal_end) {
          totalMs -= new Date(tc.time.meal_end).getTime() - new Date(tc.time.meal_start).getTime();
        }
        hours_worked = totalMs / (1000 * 60 * 60);
      }

      return {
        id: tc.id,
        status: tc.status,
        billing: tc.billing,
        time: tc.time,
        user_id: tc.user_id,
        notes: tc.notes,
        user: tc.user,
        shift: shift
          ? { id: shift.id, name: shift.name, start_time: shift.start_time, end_time: shift.end_time }
          : null,
        position: position ? { id: position.id, name: position.name } : null,
        hours_worked,
      };
    });

    return (transformed || []) as unknown as TimecardType[];
  },

  async getTimecardsByShiftId(shiftId: string): Promise<TimecardType[]> {
    const { data, error } = await supabase
      .from("time_cards")
      .select(
        `id,
        status,
        billing,
        time,
        user_id,
        shift_id,
        notes: meta_data->>note,
        user: users(id, first_name, last_name, preferred_name),
        shift_user: shift_users(
          shift_position: shift_positions(
            id,
            position_id
          )
        )`,
      )
      .eq("shift_id", shiftId);

    if (error) {
      throw new Error(`Failed to fetch timecards for shift ${shiftId}: ${error.message}`);
    }

    // Extract unique position_ids for lookup
    const positionIds = new Set<string>();
    data?.forEach((tc: any) => {
      const positionId = tc.shift_user?.shift_position?.position_id;
      if (positionId) positionIds.add(positionId);
    });

    // Fetch position names from lists.positions
    let positionsMap = new Map<string, any>();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .in("id", Array.from(positionIds));
      positions?.forEach((p) => positionsMap.set(p.id, p));
    }

    // Transform to clean flat structure
    const transformed = data?.map((tc: any) => {
      const shiftPosition = tc.shift_user?.shift_position;
      const position = shiftPosition?.position_id ? positionsMap.get(shiftPosition.position_id) : null;

      // Calculate hours_worked
      let hours_worked: number | null = null;
      if (tc.time?.start_time && tc.time?.end_time) {
        const start = new Date(tc.time.start_time);
        const end = new Date(tc.time.end_time);
        let totalMs = end.getTime() - start.getTime();
        if (tc.time.meal_start && tc.time.meal_end) {
          totalMs -= new Date(tc.time.meal_end).getTime() - new Date(tc.time.meal_start).getTime();
        }
        hours_worked = totalMs / (1000 * 60 * 60);
      }

      return {
        id: tc.id,
        status: tc.status,
        billing: tc.billing,
        time: tc.time,
        user_id: tc.user_id,
        notes: tc.notes,
        user: tc.user,
        position: position ? { id: position.id, name: position.name } : null,
        hours_worked,
      };
    });

    return (transformed || []) as unknown as TimecardType[];
  },

  // File management methods
  async uploadFile(timecardId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from timecard record
    const { data: tenantData, error: tenantError } = await supabase
      .from("time_cards")
      .select("tenant_id")
      .eq("id", timecardId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get timecard tenant: ${tenantError?.message || "Timecard not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${timecardId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("time-cards").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "time_cards", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  // Helper to infer mimetype from file extension
  _getMimeTypeFromExtension(fileName: string): string {
    const ext = fileName.split(".").pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      // Images
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
      bmp: "image/bmp",
      ico: "image/x-icon",
      // Documents
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      txt: "text/plain",
      csv: "text/csv",
      // Video
      mp4: "video/mp4",
      mov: "video/quicktime",
      avi: "video/x-msvideo",
      webm: "video/webm",
      // Audio
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
      // Archives
      zip: "application/zip",
      rar: "application/x-rar-compressed",
    };
    return mimeTypes[ext || ""] || "application/octet-stream";
  },

  async getFiles(timecardId: string): Promise<FileMetadata[]> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("time_cards")
      .select("tenant_id")
      .eq("id", timecardId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get timecard tenant: ${tenantError?.message || "Timecard not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${timecardId}`;
    const { data: files, error: listError } = await supabase.storage.from("time-cards").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list timecard files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    const fileMetadata: FileMetadata[] = files.map((file) => {
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || this._getMimeTypeFromExtension(fileName),
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "time_cards", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown",
      };
    });

    return fileMetadata;
  },

  async deleteFile(timecardId: string, fileId: string): Promise<boolean> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("time_cards")
      .select("tenant_id")
      .eq("id", timecardId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get timecard tenant: ${tenantError?.message || "Timecard not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${timecardId}`;
    const { data: files, error: listError } = await supabase.storage.from("time-cards").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list timecard files: ${listError.message}`);
    }

    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("time-cards").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(timecardId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("time_cards")
      .select("tenant_id")
      .eq("id", timecardId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get timecard tenant: ${tenantError?.message || "Timecard not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${timecardId}`;
    const { data: files, error: listError } = await supabase.storage.from("time-cards").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list timecard files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("time-cards").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "time-cards",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  // Clock-in via RPC function
  async clockIn(
    shiftId: string,
    email?: string,
  ): Promise<{
    success: boolean;
    timecard_id?: string;
    clock_in_time?: string;
    user?: { id: string; name: string };
    shift?: { id: string; name: string; location: string };
    position?: string;
    error?: string;
  }> {
    const { data, error } = await supabase.rpc("clock_in", {
      p_shift_id: shiftId,
      p_email: email || null,
    });

    if (error) {
      return { success: false, error: error.message };
    }
    return data;
  },
};
